<script lang="ts">
  import "../../app.css";
  import type { Snippet } from "svelte";
  import { onMount } from "svelte";
  import { ModeWatcher } from "mode-watcher";

  import { authStore } from "$lib/stores";
  import { writable } from "svelte/store";
  import { page } from "$app/state";

  import { MetaTags } from "$lib/components";
  import Header from "$lib/components/layout/header/headerLegacy.svelte";
  import FooterBig from "$lib/components/layout/footer/footer-big.svelte";
  import * as Sidebar from "$lib/components/ui/sidebar-custom";
  import SidebarMenu from "$lib/components/layout/sidebar";

  import CommandMenu from "$lib/components/dialogs/commandMenu.svelte";
  import GlobalHotkeys from "$lib/utility/globalHotkeys";
  import { cn } from "$lib/utility";

  import { initEnvironment } from "$lib/utility/initEnvironment";
  import ShortcutGuide from "$lib/components/basic/shortcutGuide/shortcutGuide.svelte";
  import { homepageStore, setShortcutMode } from "$lib/stores/homepageStore";
  import { tableStore } from "$lib/stores/table-store";
  import { routes } from "$lib/routes";

  let { data, children }: { data: any; children: Snippet } = $props();
  let isAboutPage = $derived(page.url.pathname.startsWith("/about"));
  let isTablesPage = $derived(page.url.pathname.startsWith(routes.tables));
  let isLandingPage = $derived(
    page.url.pathname == "/" ||
      page.url.pathname.startsWith("/features/") ||
      page.url.pathname.startsWith("/use-cases/") ||
      page.url.pathname.startsWith("/templates"),
  );

  // Initialize loadingLayout based on bot detection
  let loadingLayout = writable(!data.isBot);

  let isBlog = $derived(page.url.pathname.startsWith("/blog"));

  initEnvironment(data);

  onMount(async () => {
    // Skip initialization for bots
    if (data.isBot) {
      loadingLayout.set(false);
      return;
    }

    try {
      await authStore.initUser();
    } catch (error) {
      console.error("Error during auth initialization:", error);
    } finally {
      loadingLayout.set(false);
    }
  });
</script>

<MetaTags
  metaTitle={page.data.metaTitle || "Chatsheet | The AI Spreadsheet"}
  metaDescription={page.data.metaDescription ||
    "Chatsheet | The AI Spreadsheet. Extract PDFs, analyze files, and build custom AI's. Automate your workflow with an army of GPTs."}
  ogImage={page.data.feature_image || "/og.jpg"}
  ogType={page.data.feature_image ? "article" : "website"}
  author={page.data.author}
  canonicalUrl={"https://chatsheet.com" + page.url.pathname}
/>

<Sidebar.Provider open={false} enableKeyboardShortcut={false}>
  {#if $loadingLayout === false || data.isBot}
    <SidebarMenu collapsible={"offcanvas"} theme="light" />
    <div
      class={cn(
        "custom-scrollbar flex w-full flex-col",
        isLandingPage ? "dark:bg-theme-background-primary" : "dark:bg-theme-background-secondary",
        isAboutPage ? "dark:bg-theme-background-tertiary" : "",
      )}
    >
      <Header className="sticky top-0 flex flex-shrink-0" variant={isBlog ? "dark" : "default"} />
      <main
        class={cn(
          "w-full overflow-auto p-5 md:p-6",
          !isAboutPage ? "p-0 md:p-0" : "",
          isLandingPage ? "p-0 md:p-0" : "",
        )}
      >
        {@render children()}
      </main>
    </div>
  {/if}
</Sidebar.Provider>

<FooterBig fullWidth={true} />

<GlobalHotkeys />
<CommandMenu />
<ModeWatcher />
{#if isTablesPage}
  <ShortcutGuide isShortcutMode={$tableStore.isInShortcutMode} hideOverlay="{tableStore.setShortcutMode(false)}}" />
{:else}
  <ShortcutGuide isShortcutMode={$homepageStore.isInShortcutMode} hideOverlay="{setShortcutMode(false)}}" />
{/if}
